<template>
  <v-card width="94%" elevation="0" class="ml-5">
    <v-container>
      <v-row>
        <v-col>
          <exo-input-ref-notparametric
            :root="_self"
            name="initialOrbit"
            type="orbit"
            componentRoute="/numericalorbit"
          />
        </v-col>
        <v-col>
          <exo-input-ref-notparametric
            :root="_self"
            name="spacecraft"
            type="spacecraft"
            componentRoute="/numericalspacecraft"
          />
        </v-col>
        <v-col
          ><exo-input-date-time :root="_self" name="simulationEndDate"
        /></v-col>
      </v-row>
      <v-row>
        <v-col>
          <exo-input-select :multiple="true" :root="_self" name="perturbations">
            <exo-help-dialog title="Perturbations">
              <tooltipPerturbations />
            </exo-help-dialog>
          </exo-input-select>
        </v-col>
        <v-col>
          <exo-input-select :root="_self" name="propagationType">
            <exo-help-dialog title="Propagation type">
              Two type of propagation can be performed:
              <br />
              <br />
              <b>Numerical propagation</b>: this gives the most accurate results
              at the expenses of an increased computational load. The integrator
              used is an adaptative step Dormand-Prince which guarantees high
              precision. The maximum step is set to 100 seconds and the minimum
              one to 0.01 seconds.
              <br />
              <br />
              <b>Semi-analytical propagation</b>: this allows to propagate the
              mean orbital elements (as per the DSST theory) using a very large
              step. This results in very fast yet accurate propagations. An
              adaptative step integrator is used (Dormand-Prince). The step is
              computed as a function of the orbital period and its maximum value
              is set to 20 times the Keplerian orbital period. Eclipse, attitude
              and power system can not be simulated in semi-analytical
              propagation.
            </exo-help-dialog>
          </exo-input-select>
        </v-col>
      </v-row>
      <v-row>
        <v-expansion-panels accordion multiple v-model="expandedPanels">
          <v-expansion-panel
            v-for="(burn, index) in inputs.customBurnDefinitions"
            :key="burn.id"
            class="outlined-panel"
          >
            <v-expansion-panel-header>
              <v-row class="d-flex justify-center align-center">
                <div>Burn n°{{ index + 1 }}</div>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      v-on="on"
                      color="red"
                      @click.stop="deleteBurn(index, index)"
                      ><v-icon>delete</v-icon></v-btn
                    >
                  </template>
                  <span> Delete this burn</span>
                </v-tooltip>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <CustomBurnForm
                :ref="burn.id"
                :customBurn="burn"
                v-on:updateCustomBurn="updateBurn(index, $event)"
                v-on:updateValid="updateBurnValid(burn.id, $event)"
                :spaceObject="spaceObjectFromSpacecraft"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-row>
      <v-row class="d-flex justify-center align-center mt-2">
        <v-btn color="primary" @click="createNewBurn()"
          >Add new burn</v-btn
        ></v-row
      >
      <v-row
        ><v-btn color="primary" @click="compute()" :disabled="!inputsValid"
          >Display results
        </v-btn></v-row
      >
    </v-container>
    <v-container v-if="results != null">
      <v-row
        ><CustomManeuveringScenarioVisualizer
          :satellitePositions="results.ephemerides"
          :dates="results.dates"
          :maneuverDates="
            inputs.customBurnDefinitions.map(
              (customBurn) => customBurn.burnStartDate
            )
          "
      /></v-row>
      <v-row
        ><v-col class="d-flex justify-center align-center"
          ><v-btn color="primary" @click="exportCurrentResults()"
            >Export as .csv</v-btn
          ></v-col
        ><v-col class="d-flex justify-center align-center"
          ><v-text-field
            label="Orbit name"
            color="primary"
            v-model="finalOrbitName"
            maxlength="100"
            :rules="[orbitNameFeedback]"
            :error-messages="orbitNameErrorMessage"
          ></v-text-field></v-col
        ><v-col class="d-flex justify-center align-center"
          ><v-btn color="primary" @click="saveOrbit()"
            >Save final orbit in database</v-btn
          ></v-col
        ></v-row
      >
    </v-container>
  </v-card>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import dataTools from '../../tools/data.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import numericalMixin from '../../mixins/numericalForms.js'
import errorMixin from '../../mixins/logHandler.js'
import CustomBurnForm from '../../views/Asterx/CustomBurnForm'
import tooltipPerturbations from '../../components/Tooltips/tooltipPerturbations'
import CustomManeuveringScenarioVisualizer from '../../views/Asterx/CustomManeuveringScenarioVisualizer'
import { v4 as uuid } from 'uuid'

export default {
  name: 'CustomScenarioSimulation',

  components: {
    CustomBurnForm,
    tooltipPerturbations,
    CustomManeuveringScenarioVisualizer
  },

  mixins: [formMixin, numericalMixin, errorMixin],

  mounted() {
    this.refreshSpacecrafts()
    this.refreshNumericalOrbits()
    this.init()
  },

  props: {},

  watch: {},

  computed: {
    allPropertiesList() {
      return [
        ...this.getOptionalPropertiesList(this.inputs),
        ...this.getRequiredPropertiesList(this.inputs)
      ]
    },
    burnsValid() {
      return (
        this.inputs.customBurnDefinitions.length > 0 &&
        !this.inputs.customBurnDefinitions.some(
          (burn) => this.validBurns[burn.id] !== true
        )
      )
    },
    inputsValid() {
      let fieldsState = this.verifyPropertiesList(
        this.getRequiredPropertiesList(this.inputs),
        this.inputs,
        true
      )
      fieldsState =
        fieldsState &&
        this.verifyPropertiesList(
          this.getOptionalPropertiesList(this.inputs),
          this.inputs,
          false
        )
      // if impossible to validate a form : uncomment the code in verifyPropertiesList to understand the problematic field
      return fieldsState && this.burnsValid
    },
    spaceObjectFromSpacecraft() {
      return {
        noradID: '99999',
        sphericalCrossSection: 0.23,
        totalMass: this.inputs.spacecraft?.platformMass,
        propellantMass: this.inputs.spacecraft?.thruster?.totalMass,
        thrust: this.inputs.spacecraft?.thruster?.thrust,
        isp: this.inputs.spacecraft?.thruster?.isp
      }
    },
    orbitNameFeedback() {
      let feedback = true
      if (this.orbitNameState !== null && !this.orbitNameState) {
        feedback =
          "Please only use the allowed characters : a-z A-Z 0-9 ' ',.-+=/_#@$&:%|()]["
      }
      return feedback
    },
    orbitNameState() {
      let valid = null
      if (this.finalOrbitName !== '') {
        let allowedChars = RegExp(
          '^[a-zA-Z0-9 ,\\.\\-\\+=\\/_@#\\$&:%\\|\\(\\)\\[\\]]+$'
        )
        valid = allowedChars.test(this.newOrbitName)
      }
      return valid
    }
  },

  data() {
    return {
      showModal: false,
      enableFormCache: false,

      results: null,
      finalOrbitName: '',
      orbitNameErrorMessage: null,

      enumPropertiesList: this.getEnumProperties(),
      orbitsNotParametrics: [],
      spacecraftsNotParametrics: [],
      expandedPanels: [],
      validBurns: {},

      inputs: {
        customBurnDefinitions: [],
        initialOrbit: this.getDefaultValue('initialOrbit'),
        spacecraft: this.getDefaultValue('spacecraft'),
        propagationType: this.getDefaultValue('propagationType'),
        perturbations: this.getDefaultValue('perturbations'),
        simulationEndDate: this.getDefaultValue('simulationEndDate')
      }
    }
  },

  methods: {
    init() {
      this.expandedPanels = this.inputs.customBurnDefinitions.map(
        (_, index) => index
      )
    },
    async saveOrbit() {
      return this.$http
        .post('/users/' + this.currentUser.id + '/orbitdefinitions', {
          ...this.results.finalOrbit,
          name: this.finalOrbitName
        })
        .then((response) => {
          this.orbitNameErrorMessage = null
          this.displayMessage(
            'Orbit saved successfully.',
            '',
            'notification',
            'success'
          )
          return response.data
        })
        .catch((error) => {
          if (error.response && error.response.status === 409) {
            this.orbitNameErrorMessage = `An orbit with the name ${this.finalOrbitName} already exists in the database.`
          } else {
            this.displayMessage(
              'Error while saving orbit in database',
              error,
              'notification',
              'error'
            )
            return null
          }
        })
    },

    refreshTable() {},

    getSubTypes(instance) {
      return null
    },

    getOptionalPropertiesList(instance) {
      let subTypes = this.getSubTypes(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        subTypes,
        'optional'
      )
    },

    getRequiredPropertiesList(instance) {
      let subTypes = this.getSubTypes(instance)
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        subTypes,
        'required'
      )
      return requiredList
    },

    refreshSpacecrafts() {
      let self = this
      this.$http
        .get(this.getAPI('spacecraftdefinitions'))
        .then((response) => {
          self.spacecraftsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of spacecrafts. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    refreshNumericalOrbits() {
      let self = this
      this.$http
        .get(this.getAPI('orbitdefinitions'))
        .then((response) => {
          self.orbitsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of numerical orbits. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    getCurrentFormType() {
      return 'CustomManeuveringScenarioRequest'
    },

    getRefList(param) {
      if (param === 'spaceraft') {
        return this.spacecraftsNotParametrics
      }
    },
    async updateOpenedPanelsAfterDeletion(deletedIndex) {
      this.expandedPanels = this.expandedPanels
        .filter((panelIndex) => panelIndex !== deletedIndex)
        .map((panelIndex) => {
          if (panelIndex > deletedIndex) {
            return panelIndex - 1
          } else {
            return panelIndex
          }
        })
    },
    updateBurn(indexToUpdate, newValue) {
      this.inputs.customBurnDefinitions.splice(indexToUpdate, 1, newValue)
    },
    updateBurnValid(id, isValid) {
      this.validBurns = { ...this.validBurns, [id]: isValid }
    },
    deleteBurn(indexToDelete) {
      this.inputs.customBurnDefinitions.splice(indexToDelete, 1)
      this.$nextTick().then(() =>
        this.updateOpenedPanelsAfterDeletion(indexToDelete)
      )
    },
    createNewBurn() {
      this.expandedPanels.push(this.inputs.customBurnDefinitions.length)
      this.inputs.customBurnDefinitions.push({ id: uuid() })
    },
    async compute() {
      this.results = await this.$http
        .post('/physics/custommaneuveringscenario', this.inputs)
        .then(function (response) {
          return {
            ephemerides: response.data.ephemerides,
            dates: response.data.dates,
            finalOrbit: response.data.finalOrbit,
            frame: response.data.frame
          }
        })
        .catch((e) => {
          self.displayMessage(
            'Error during calculations. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )

          return null
        })
    },
    exportCurrentResults() {
      const title = `Custom scenario`
      let fileContent = 'date,X,Y,Z,VX,VY,VZ\n'
      for (let i = 0; i < this.results.dates.length; i++) {
        let currentLine = `${this.results.dates[i]}`
        for (let j = 0; j < 6; j++) {
          currentLine += ',' + this.results.ephemerides[i][j]
        }
        currentLine += '\n'
        fileContent += currentLine
      }
      dataTools.downloadFile(title, fileContent, 'csv')
    }
  }
}
</script>

<style scoped>
.outlined-panel {
  border: 1px solid;
}
</style>
